import type {
  Immunization as FhirImmunization,
  Patient,
  Practitioner,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { z } from 'zod'
import {
  CVX_SYSTEM,
  IMMUNIZATION_PERFORMER_FUNCTION_SYSTEM,
  ImmunizationPerformerFunction,
} from '#src/codeSystems.ts'
import { elationSandboxIdentifier, extractElationId } from '#src/elation.ts'
import { invariant } from '#src/invariant.ts'
import { addFhirRef, metadataSchema } from '#src/models/elation/metadata.ts'
import { ExtendedImmunization } from '#src/models/immunizations/ExtendedImmunization.ts'
import { createReference } from '#src/references.ts'
import { now } from '#src/utils.ts'

// https://docs.elationhealth.com/reference/the-immunization-object-wip

// Vaccine schema for nested vaccine information
export const vaccineSchema = z.object({
  name: z.string().max(200),
  cvx: z.number().int().positive().nullable(),
  cdc_name: z.string().max(200),
  cdc_type: z.string().max(100),
})

export type Vaccine = z.infer<typeof vaccineSchema>

// Publicity code enum from CDC value set
export const publicityCodeEnum = z.enum([
  'No reminder/recall',
  'Reminder/recall - any method',
  'Reminder/recall - no calls',
  'Reminder only - any method',
  'Reminder only - no calls',
  'Recall only - any method',
  'Recall only - no calls',
  'Reminder/recall - to provider',
  'Reminder to provider',
  'Only reminder to provider, no recall',
  'Recall to provider',
  'Only recall to provider, no reminder',
] as const)

// VFC eligibility enum from CDC value set and state/local-specific codes
export const vfcEligibilityEnum = z.enum([
  'Not VFC eligible',
  'VFC eligible - Medicaid/Medicaid Managed Care',
  'VFC eligible - Uninsured',
  'VFC eligible - American Indian/Alaskan Native',
  'VFC eligible - Under-insured)',
  'Local-specific eligibility',
  'CA State General Fund Vaccines',
  'OR Special Projects',
  'OR Locally Owned',
  'HI Birthing Hospital Hepatitis B',
  'HI Adult Insured',
  'HI Adult Not Insured',
] as const)

// Information source enum from CDC value set
export const infoSourceEnum = z.enum([
  'New immunization record',
  'Historical information - source unspecified',
  'Historical information - from other provider',
  "Historical information - from parent's written record",
  "Historical information - from parent's recall",
  'Historical information - from other registry',
  'Historical information - from birth certificate',
  'Historical information - from school record',
  'Historical information - from public agency',
] as const)

export const immunizationSchema = z.object({
  id: z.number().int().positive().optional().nullable(),
  administered_date: z.string().datetime(),
  administering_physician: z.number().int().positive().nullable(),
  ordering_physician: z.number().int().positive(),
  description: z.string().max(500).nullable(),
  reason: z.string().max(500).optional().nullable(),
  vaccine: vaccineSchema,
  qty: z.string().optional().nullable(),
  qty_units: z.string().max(20),
  lot_number: z.string().max(100).optional().nullable(),
  manufacturer_name: z.string().max(200).optional().nullable(),
  manufacturer_code: z.string().max(50).optional().nullable(),
  expiration_date: z.string().datetime().optional().nullable(),
  vis: z.string().max(100).optional().nullable(),
  method: z.string().max(100).nullable(),
  site: z.string().max(100).nullable(),
  notes: z.string().max(1000).optional().nullable(),
  publicity_code: publicityCodeEnum.optional().nullable(),
  vfc_eligibility: vfcEligibilityEnum.optional().nullable(),
  funding_source: z.string().max(100).optional().nullable(),
  info_source: infoSourceEnum.optional().nullable(),
  allowed_sharing: z.boolean().optional().nullable(),
  practice: z.number().int().positive(),
  patient: z.number().int().positive(),
  created_date: z.string().datetime().optional().nullable(),
  deleted_date: z.string().datetime().optional().nullable(),
  dose_in_series: z.number().int().positive().optional().nullable(),
  priority_population: z.string().max(100).optional().nullable(),
  ndc: z.string().max(20).optional().nullable(),
  patient_consent: z.boolean().optional().nullable(),
  metadata: metadataSchema.optional().nullable(),
})

export type Immunization = z.infer<typeof immunizationSchema>

// Create schema for new immunizations (excludes read-only fields)
export const createImmunizationSchema = immunizationSchema.omit({
  id: true,
  created_date: true,
  deleted_date: true,
  metadata: true,
})

export type CreateImmunization = z.infer<typeof createImmunizationSchema>

// Update schema for partial updates
export const updateImmunizationSchema = createImmunizationSchema.partial()

export type UpdateImmunization = z.infer<typeof updateImmunizationSchema>

export function fromFhir(
  immunization: FhirImmunization,
  patient: Patient,
  practitioner: Practitioner
): Omit<Immunization, 'id'> & { id?: number } {
  const id = extractElationId(immunization)
  const patientId = extractElationId(patient)
  const practitionerId = extractElationId(practitioner)

  invariant(patientId, 'Patient ID not found')
  invariant(practitionerId, 'Practitioner ID not found')

  // Create ExtendedImmunization instance to leverage Extension decorator
  const extendedImmunization =
    ExtendedImmunization.fromFhirResource(immunization)

  // Extract vaccine information from code
  const vaccineCode = immunization.vaccineCode?.coding?.[0]
  const vaccineName =
    vaccineCode?.display ?? immunization.vaccineCode?.text ?? 'Unknown Vaccine'
  const cvxCode = vaccineCode?.code ? parseInt(vaccineCode.code, 10) : 0

  // Extract administration site and route
  const site = immunization.site?.text ?? 'Unknown Site'
  const route = immunization.route?.text ?? 'Unknown Method'

  // Extract lot number and manufacturer
  const lotNumber = immunization.lotNumber ?? ''
  const manufacturer = immunization.manufacturer?.display ?? ''

  // Extract quantity information
  const quantity = immunization.doseQuantity
  const qty = quantity?.value?.toString() ?? null
  const qtyUnits = quantity?.unit ?? 'ml'

  // Extract series information from extension
  const doseInSeries = extendedImmunization.series
    ? parseInt(extendedImmunization.series, 10)
    : null

  // Extract expiration date
  const expirationDate = immunization.expirationDate

  // Extract VIS information
  const vis = immunization.education?.[0]?.documentType ?? ''

  // Extract notes
  const notes = immunization.note?.[0]?.text ?? ''

  // Extract reason
  const reason = immunization.reasonCode?.[0]?.text ?? ''

  // Extract extension values using ExtendedImmunization properties
  const vfcEligibility = vfcEligibilityEnum.safeParse(
    extendedImmunization.vfcEligibility
  )
  const fundingSource = extendedImmunization.fundingSource
  const infoSource = infoSourceEnum.safeParse(
    extendedImmunization.informationSource
  )
  const publicityCode = publicityCodeEnum.safeParse(
    extendedImmunization.publicityCode
  )
  const patientConsent = extendedImmunization.patientConsent
  const allowedSharing = extendedImmunization.allowedSharing
  const priorityPopulation = extendedImmunization.priorityPopulation
  const ndc = extendedImmunization.ndc

  const result: Omit<Immunization, 'id'> & { id?: number } = {
    id,
    administered_date: immunization.occurrenceDateTime ?? now(),
    administering_physician: practitionerId,
    ordering_physician: practitionerId,
    description: vaccineName,
    reason: reason ?? null,
    vaccine: {
      name: vaccineName,
      cvx: cvxCode,
      cdc_name: vaccineCode?.display ?? vaccineName,
      cdc_type: vaccineCode?.display ?? vaccineName,
    },
    qty,
    qty_units: qtyUnits,
    lot_number: lotNumber ?? null,
    manufacturer_name: manufacturer ?? null,
    manufacturer_code: null,
    expiration_date: expirationDate ?? null,
    vis: vis ?? null,
    method: route,
    site,
    notes: notes ?? null,
    publicity_code: publicityCode.success ? publicityCode.data : null,
    vfc_eligibility: vfcEligibility.success ? vfcEligibility.data : null,
    funding_source: fundingSource ?? null,
    info_source: infoSource.success ? infoSource.data : null,
    allowed_sharing: allowedSharing ?? null,
    practice: 0, // Will be set by caller
    patient: patientId,
    created_date: now(),
    deleted_date: null,
    dose_in_series: doseInSeries,
    priority_population: priorityPopulation ?? null,
    ndc: ndc ?? null,
    patient_consent: patientConsent ?? null,
  }

  return addFhirRef(result, immunization)
}

export function toFhir({
  immunization,
  patient,
  orderingPractitioner,
  administeringPractitioner,
}: {
  immunization: Immunization
  patient: Patient
  orderingPractitioner?: Practitioner
  administeringPractitioner?: Practitioner
}): FhirImmunization {
  // Create ExtendedImmunization instance to leverage Extension decorator
  const extendedImmunization = new ExtendedImmunization({
    identifier: immunization.id
      ? [elationSandboxIdentifier(immunization.id.toString())]
      : [],
    status: 'completed',
    vaccineCode: {
      coding: [
        {
          system: immunization.vaccine.cvx ? CVX_SYSTEM : undefined,
          code: immunization.vaccine.cvx
            ? immunization.vaccine.cvx?.toString()
            : undefined,
          display:
            immunization.vaccine.cdc_name ?? immunization.vaccine.name ?? '',
        },
      ],
      text: immunization.description ?? undefined,
    },
    patient: createReference(patient),
    occurrenceDateTime: immunization.administered_date,
    recorded: immunization.created_date ?? now(),
    primarySource: immunization.info_source === 'New immunization record',
    location: undefined, // Not available in Elation model
    manufacturer: immunization.manufacturer_name
      ? {
          display: immunization.manufacturer_name,
        }
      : undefined,
    lotNumber: immunization.lot_number ?? undefined,
    expirationDate: immunization.expiration_date ?? undefined,
    site: immunization.site
      ? {
          text: immunization.site,
        }
      : undefined,
    route: immunization.method
      ? {
          text: immunization.method,
        }
      : undefined,
    doseQuantity: immunization.qty
      ? {
          value: parseFloat(immunization.qty),
          unit: immunization.qty_units,
          system: 'http://unitsofmeasure.org',
          code: immunization.qty_units,
        }
      : undefined,
    performer: compact([
      administeringPractitioner && {
        actor: createReference(administeringPractitioner),
        function: {
          text: 'Administering Provider',
          coding: [
            {
              system: IMMUNIZATION_PERFORMER_FUNCTION_SYSTEM,
              code: ImmunizationPerformerFunction.AdministeringProvider,
              display: 'Administering Provider',
            },
          ],
        },
      },
      orderingPractitioner && {
        actor: createReference(orderingPractitioner),
        function: {
          text: 'Ordering Provider',
          coding: [
            {
              system: IMMUNIZATION_PERFORMER_FUNCTION_SYSTEM,
              code: ImmunizationPerformerFunction.OrderingProvider,
              display: 'Ordering Provider',
            },
          ],
        },
      },
    ]),
    reasonCode: immunization.reason
      ? [
          {
            text: immunization.reason,
          },
        ]
      : undefined,
    note: immunization.notes
      ? [
          {
            text: immunization.notes,
          },
        ]
      : undefined,
    education: immunization.vis
      ? [
          {
            documentType: immunization.vis,
          },
        ]
      : undefined,
    text: {
      status: 'generated',
      div: `<div xmlns="http://www.w3.org/1999/xhtml">${immunization.description}</div>`,
    },
  })

  // Set extension values using the Extension decorator properties
  extendedImmunization.vfcEligibility =
    immunization.vfc_eligibility ?? undefined
  extendedImmunization.fundingSource = immunization.funding_source ?? undefined
  extendedImmunization.informationSource = immunization.info_source ?? undefined
  extendedImmunization.publicityCode = immunization.publicity_code ?? undefined
  extendedImmunization.patientConsent =
    immunization.patient_consent ?? undefined
  extendedImmunization.allowedSharing =
    immunization.allowed_sharing ?? undefined
  extendedImmunization.priorityPopulation =
    immunization.priority_population ?? undefined
  extendedImmunization.ndc = immunization.ndc ?? undefined
  extendedImmunization.series =
    immunization.dose_in_series?.toString() ?? undefined
  extendedImmunization.externalResourceJSON = JSON.stringify(immunization)

  return extendedImmunization.toFhirResource()
}
